import React, { useState, useMemo, useRef, Fragment, useCallback } from 'react';
import classnames from 'classnames';
import { ResponsiveLine } from '@nivo/line';

import Lazy from '../components/Lazy';
import {
	filterByKey,
	cumulate,
	filterGroupsData,
	sliceGroupsData,
	countGroupsMinMax,
	prettyNumber,
	getCumulatedExcessMortalityCSV,
} from '../utils/graphs';
import MultiSelect, { arrayToOptions } from '../components/MultiSelect';
import { axisRight, ZeroLineLayer, getToolTip, theme } from '../components/NivoHelpers';
import RangeSelector from '../components/RangeSelector';
import Section, { Full, Left, Right, Sticky } from '../components/Section';
import Toggle from '../components/Toggle';

import { uid, ageGroups, getAgeOptions, downloadFile } from '../utils/data';

import cssVars from '../styles/cssVars';
import shared from '../styles/shared.module.css';
import css from './ExcessMortality.module.css';

const colors = [cssVars.dataBlue, '#EFC000', '#868686', '#CD534C', '#8F7700', '#7AA7DC', '#267D25'];

export const ExcessMortality = ({ data, ages, printing }) => {
	const id = useMemo(() => uid('cumu'), []);
	const years = useMemo(() => data.data[0].data.map((c) => c.id), [data]);
	const yearColors = useMemo(() => {
		const obj = {};
		for (let i = 0; i < years.length; i++) {
			const colIndex = years.length - 1 - i;
			obj[years[i]] = colors[colIndex % colors.length];
		}
		return obj;
	}, [years]);
	const ageOptions = useMemo(() => getAgeOptions(ages), [ages]);
	const yearOptions = useMemo(() => arrayToOptions(years), [data]);
	const [selectedAges, setAges] = useState(ageOptions);
	const [selectedYears, setYears] = useState(yearOptions.slice(yearOptions.length - 3, yearOptions.length));
	const selectedYearColors = useMemo(
		() => selectedYears.map((year) => yearColors[year.value]),
		[selectedYears, yearColors]
	);
	const [range, setRange] = useState({ min: 0, max: 52 });
	const [saving, setSaving] = useState(false);
	const [showCumulated, setCumulated] = useState(true);
	const graphRef = useRef(null);
	const handleToggle = (n, v) => {
		setCumulated(v);
	};

	// Filter age groups
	let filtered = useMemo(
		() =>
			filterByKey(
				data.data,
				'id',
				selectedAges.map((a) => a.value)
			),
		[selectedAges, data]
	);

	// Filter years
	filtered = useMemo(
		() =>
			filterGroupsData(
				filtered,
				selectedYears.map((y) => y.value)
			),
		[selectedYears, filtered]
	);

	// Filter Weeks
	filtered = useMemo(() => sliceGroupsData(filtered, range.min, range.max), [filtered, range]);

	// Calculate Cumulative
	// Oh maaaaan, this is why we should use immutable
	filtered = useMemo(() => {
		if (!showCumulated) {
			return filtered;
		}
		const newData = [];
		for (let i = 0; i < filtered.length; i++) {
			const newAge = {
				id: filtered[i].id,
				data: [],
			};
			for (let j = 0; j < filtered[i].data.length; j++) {
				newAge.data.push({
					id: filtered[i].data[j].id,
					data: cumulate(filtered[i].data[j].data, 'y'),
				});
			}
			newData.push(newAge);
		}
		return newData;
	}, [showCumulated, filtered]);

	const [minY, maxY, minMaxVals, minX, maxX] = useMemo(() => countGroupsMinMax(filtered, false), [filtered]);

	const onDownloadData = useCallback(
		(filename) => {
			const csv = getCumulatedExcessMortalityCSV(filtered);
			downloadFile(filename + '.csv', csv);
		},
		[filtered]
	);

	const graphs = [];
	for (let i = 0; i < filtered.length; i++) {
		const isTotal = filtered[i].id == 'Total';
		graphs.push(
			<Lazy key={`${id}-${i}`} height={isTotal ? 240 + 90 : 200 + 50} show={printing || saving}>
				<Graph
					height={isTotal ? 240 : 200}
					minX={minX}
					maxX={maxX}
					minY={minMaxVals[i].min}
					maxY={minMaxVals[i].max}
					ruler={filtered[i].group == 'Total'}
					item={filtered[i]}
					range={range}
					id={id}
					colors={selectedYearColors}
				/>
			</Lazy>
		);
	}

	return (
		<Section id={'excess-mortality'} saving={saving}>
			<Full title="Excess mortality">
				<p>
					Graphs showing the weekly excess deaths (deviation in mortality from the expected level) in the
					data-providing EuroMOMO partner countries for the past years, all ages and by age groups.
				</p>
				<p className={css.breaking}>
					CAUTION: Please note that the cumulated excess mortality outputs for the COVID-19 pandemic period
					may be unreliable, due to some model issues. See more information and technical explanations in the
					EuroMOMO weekly bulletin
				</p>
			</Full>
			<Left
				filename={'charts-excess-mortality'}
				imageRef={graphRef}
				setSaving={setSaving}
				sticky
				onDownloadData={onDownloadData}>
				<MultiSelect
					hideOnPrint
					wideLabels
					className={css.select}
					label="Age groups"
					value={selectedAges}
					options={ageOptions}
					onChange={setAges}
				/>
				<MultiSelect
					hideOnPrint
					wideLabels
					className={css.select}
					label="Years"
					value={selectedYears}
					options={yearOptions}
					onChange={setYears}
				/>
			</Left>
			<Right forwardRef={graphRef}>
				<Sticky>
					<div className={classnames(shared.row, { [shared.hidden]: saving })}>
						<RangeSelector
							label="Week range"
							className={css.range}
							min={0}
							max={52}
							value={range}
							onChange={setRange}
							formatLabel={(v) => 'Week ' + v}
							fps={30}
						/>
						<Toggle
							className={css.toggle}
							label="Show"
							name={'toggle'}
							value={showCumulated}
							onChange={handleToggle}
							innerLabels={['Weekly', 'Cumulated']}
						/>
					</div>
					<div className={shared.legend}>
						<div className={shared.item}>
							<span
								className={shared.line}
								style={{ borderColor: cssVars.baselineBlack, borderStyle: 'dashed', borderWidth: 1 }}
							/>
							<h5>Baseline</h5>
						</div>
						{selectedYears
							.slice()
							.sort((a, b) => a.value - b.value)
							.map((year) => (
								<div className={shared.item} key={`${id}-${year.value}`}>
									<span className={shared.line} style={{ borderColor: yearColors[year.value] }} />
									<h5>{year.value}</h5>
								</div>
							))}
					</div>
				</Sticky>
				<div className={shared.graphsContainer}>{graphs}</div>
			</Right>
		</Section>
	);
};

export default ExcessMortality;

const margin = { top: 10, right: 10, bottom: 18, left: 60 };
const xScale = { type: 'linear', min: 'auto', max: 'auto' };
const axisBottom = {
	orient: 'bottom',
	tickSize: 5,
	tickPadding: 0,
	legend: '',
	legendOffset: 56,
	legendPosition: 'middle',
};

const tooltip = ({ point }) =>
	getToolTip(`${point.serieId}, Week ${point.data.x}`, prettyNumber(point.data.yFormatted, false));

const Graph = ({ item, id, height, minY, maxY, minX, maxX, range, colors, ruler }) => {
	if (item.data.length === 0) {
		return null;
	}
	const yTicks = item.id === 'Total' ? 6 : 5;
	return (
		<div className={classnames(css.chartContainer, css[`chart${item.id}`])}>
			<h4>{ageGroups[item.id] || item.id}</h4>
			<div style={{ height }} className={css.chart}>
				<ResponsiveLine
					id={id}
					data={item.data}
					colors={colors}
					theme={theme}
					minX={minX}
					maxX={maxX}
					minY={minY}
					maxY={maxY}
					margin={margin}
					xScale={xScale}
					yScale={{
						type: 'linear',
						min: minY,
						max: maxY,
					}}
					axisTop={null}
					axisRight={axisRight}
					axisBottom={axisBottom}
					axisLeft={{
						format: (num) => prettyNumber(num, false),
						orient: 'left',
						tickSize: 5,
						tickValues: yTicks,
						tickPadding: 3,
						tickRotation: 0,
						legend: 'Excess deaths',
						legendOffset: -55,
						legendPosition: 'middle',
					}}
					gridYValues={yTicks}
					enableGridX={false}
					enableGridY={true}
					lineWidth={2}
					enablePoints={false}
					useMesh={true}
					animate={false}
					tooltip={tooltip}
					layers={[
						'grid',
						'markers',
						'areas',
						ZeroLineLayer,
						'lines',
						'axes',
						'points',
						'legends',
						'mesh',
						'slices',
					]}
				/>
			</div>
		</div>
	);
};
